import { WorkspaceIntegration, WorkspaceIntegrationStatuses } from '@prisma/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { client } from '../../../client';

export const useGetOAuthUrl = (connectionName: string) => {
  return useQuery({
    queryKey: ['oauth-url', connectionName],
    queryFn: async () => {
      const response = await client.get<{ url: string }>(`/integrations/install/${connectionName}`);
      return response.data;
    },
    enabled: !!connectionName,
  });
};

export const useGetIntegration = (name: string) => {
  return useQuery({
    queryKey: ['integration', name],
    queryFn: async () => {
      const response = await client.get<WorkspaceIntegration>(`/integrations/${name}`);
      return response.data;
    },
    enabled: !!name,
  });
};

export const useGetMyIntegrations = () => {
  return useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await client.get<WorkspaceIntegration[]>('/integrations/my');
      return response.data;
    },
  });
};

export const useDisconnectIntegration = (onSuccess?: () => void, onError?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (integrationName: string) => {
      const response = await client.delete(`/integrations/disconnect/${integrationName}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useToggleIntegration = (onSuccess?: () => void, onError?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ integrationName, isActive }: { integrationName: string; isActive: boolean }) => {
      const status = isActive ? WorkspaceIntegrationStatuses.Enable : WorkspaceIntegrationStatuses.Disable;
      const response = await client.put(`/integrations/${integrationName}/toggle/${status}`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useGetIntegrationUsers = (integrationName: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['integration-users', integrationName],
    queryFn: async () => {
      const response = await client.get(`/integrations/${integrationName}/users`);
      return response.data;
    },
    enabled: enabled && !!integrationName,
  });
};

export const useGetIntegrationProjects = (integrationName: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['integration-projects', integrationName],
    queryFn: async () => {
      const response = await client.get(`/integrations/${integrationName}/projects`);
      return response.data;
    },
    enabled: enabled && !!integrationName,
  });
};

export const useExportProjects = (
  integrationName: string,
  onSuccess?: (data: any) => void,
  onError?: () => void
) => {
  return useMutation({
    mutationFn: async (projectIds: string[]) => {
      const response = await client.post(`/integrations/${integrationName}/export-projects`, { projectIds });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useExportUsers = (
  integrationName: string,
  onSuccess?: (data: any) => void,
  onError?: () => void
) => {
  return useMutation({
    mutationFn: async (userIds: string[]) => {
      const response = await client.post(`/integrations/${integrationName}/export-users`, { userIds });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export interface IntegrationWebhook {
  key: string;
  webhookId: string | number;
  events: string[];
  url?: string;
}

export const useGetIntegrationWebhooks = (integrationName: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['integration-webhooks', integrationName],
    queryFn: async () => {
      const response = await client.get<IntegrationWebhook[]>(`/integrations/${integrationName}/webhooks`);
      return response.data;
    },
    enabled: enabled && !!integrationName,
    staleTime: 0,
  });
};

export const useDeleteWebhook = (integrationName: string, onSuccess?: () => void, onError?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (webhookKey: string) => {
      const response = await client.delete(`/integrations/${integrationName}/webhooks/${webhookKey}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-webhooks', integrationName] });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useDeleteAllWebhooks = (
  integrationName: string,
  onSuccess?: () => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await client.delete(`/integrations/${integrationName}/webhooks`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-webhooks', integrationName] });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useGetIntegrationChannels = (integrationName: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['integration-channels', integrationName],
    queryFn: async () => {
      const response = await client.get(`/integrations/${integrationName}/channels`);
      return response.data;
    },
    enabled: enabled && !!integrationName,
  });
};

export const useSetAlertsChannel = (
  integrationName: string,
  onSuccess?: () => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelId: string) => {
      const response = await client.put(`/integrations/${integrationName}/channels/alerts`, { channelId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration', integrationName] });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useSetListenChannels = (
  integrationName: string,
  onSuccess?: () => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelIds: string[]) => {
      const response = await client.put(`/integrations/${integrationName}/channels/listen`, { channelIds });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration', integrationName] });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export async function openConnection(connectionName: string) {
  // Get the OAuth URL with the correct connection name
  const {
    data: { url },
  } = await client.get<{ url: string }>(`/integrations/install/${connectionName}`);
  if (!url) {
    return false;
  }

  const getPopupOptions = (connectionName: string) => {
    const width = 600;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    return `width=${width},height=${height},left=${left},top=${top},toolbar=0,scrollbars=1,status=1,resizable=1,location=1,menuBar=0,titlebar=1,title=${connectionName} Connection`;
  };
  return new Promise<boolean>((resolve, reject) => {
    try {
      const popup = window.open(url, `${connectionName}_oauth`, getPopupOptions(connectionName));
      if (!popup) {
        throw new Error('Failed to open popup window');
      }

      popup.focus();

      const checkPopup = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          resolve(true);
        }
      }, 1000);

      // Cleanup interval after 5 minutes to prevent memory leaks
      setTimeout(
        () => {
          clearInterval(checkPopup);
          if (popup && !popup.closed) {
            popup.close();
          }
          reject(new Error('OAuth connection timeout'));
        },
        5 * 60 * 1000
      );
    } catch (error) {
      reject(error);
    }
  });
}
